(function () {
  'use strict';

  if (customElements.get('modal-dialog')) return;

  var focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class ModalDialog extends HTMLElement {
    constructor() {
      super();
      this.opener = null;
      this.focusFrame = null;
      this.cartLinkRequest = 0;
      this.cartLinkReady = false;
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleTrigger = (event) => {
        var trigger = event.target.closest && event.target.closest(this.triggerSelector);
        if (!trigger) return;
        event.preventDefault();
        this.opener = trigger;
        this.open();
      };
      this.handleClose = () => this.close();
      this.handleBackdrop = (event) => {
        if (event.target === this.modal) this.close();
      };
      this.handleCartLinkRetry = () => this.loadCartLink();
      this.handleSubmit = (event) => {
        if (this.cartLinkField && !this.cartLinkReady) {
          event.preventDefault();
          return;
        }
        this.dispatchEvent(new CustomEvent('modal:formSubmit', {
          bubbles: true,
          detail: { form: this.form }
        }));
      };
    }

    connectedCallback() {
      this.modal = this.querySelector('.modal, [data-modal-overlay]');
      this.modalContent = this.querySelector('.modal-content, [data-modal-content]');
      this.closeButton = this.querySelector('.modal-close, [data-modal-close]');
      this.form = this.querySelector('form');
      this.cartLinkField = this.querySelector('#sharecartCartLink');
      this.cartLinkStatus = this.querySelector('[data-cart-link-status]');
      this.cartLinkRetry = this.querySelector('[data-cart-link-retry]');
      this.cartLinkSubmit = this.querySelector('[data-cart-link-submit]');
      if (!this.hasAttribute('role')) this.setAttribute('role', 'dialog');
      if (!this.hasAttribute('aria-modal')) this.setAttribute('aria-modal', 'true');
      if (!this.hasAttribute('aria-hidden')) this.setAttribute('aria-hidden', 'true');
      if (this.modalContent && !this.modalContent.hasAttribute('tabindex')) {
        this.modalContent.setAttribute('tabindex', '-1');
      }
      var triggerSelector = '[data-modal-trigger="' + this.id + '"], a[href*="#' + this.id + '"]';
      var extraTriggers = this.getAttribute('data-modal-trigger-selector');
      if (extraTriggers) triggerSelector += ', ' + extraTriggers;
      this.triggerSelector = triggerSelector;
      this.refreshTriggers();
      this.bindEvents();
      if (this.querySelector('[data-modal-result]')) this.open();
    }

    refreshTriggers() {
      this.modalTriggers = document.querySelectorAll(this.triggerSelector);
      this.modalTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-controls', this.id);
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', String(Boolean(this.isOpen())));
      });
    }

    bindEvents() {
      document.addEventListener('click', this.handleTrigger, true);

      if (this.closeButton) {
        this.closeButton.addEventListener('click', this.handleClose);
      }

      if (this.modal) {
        this.modal.addEventListener('click', this.handleBackdrop);
      }

      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit);
      }
      if (this.cartLinkRetry) this.cartLinkRetry.addEventListener('click', this.handleCartLinkRetry);

      document.addEventListener('keydown', this.handleKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('click', this.handleTrigger, true);
      if (this.closeButton) this.closeButton.removeEventListener('click', this.handleClose);
      if (this.modal) this.modal.removeEventListener('click', this.handleBackdrop);
      if (this.form) this.form.removeEventListener('submit', this.handleSubmit);
      if (this.cartLinkRetry) this.cartLinkRetry.removeEventListener('click', this.handleCartLinkRetry);
      this.cartLinkRequest++;
      window.cancelAnimationFrame(this.focusFrame);
    }

    handleKeydown(event) {
      if (!this.isOpen()) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      var focusable = Array.prototype.filter.call(
        this.querySelectorAll(focusableSelector),
        function (element) {
          return !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0;
        }
      );

      if (!focusable.length) {
        event.preventDefault();
        this.modalContent.focus();
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (focusable.indexOf(document.activeElement) === -1) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open() {
      if (!this.modal || this.isOpen()) return;

      this.refreshTriggers();
      if (!this.opener) this.opener = this.modalTriggers[0] || null;
      this.setAttribute('aria-hidden', 'false');
      this.modal.classList.add('open');
      this.modalTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));

      // not focusable until the open transition has applied
      var initialFocus = this.querySelector('[data-modal-result]') || this.querySelector('[data-modal-initial-focus]') || this.modalContent;
      var attempts = 0;
      var takeFocus = () => {
        if (!this.isConnected || !this.isOpen() || !initialFocus) return;
        initialFocus.focus({ preventScroll: true });
        if (document.activeElement !== initialFocus && ++attempts < 20) {
          this.focusFrame = window.requestAnimationFrame(takeFocus);
        }
      };
      window.cancelAnimationFrame(this.focusFrame);
      this.focusFrame = window.requestAnimationFrame(takeFocus);

      if (this.cartLinkField) this.loadCartLink();

      this.dispatchEvent(new CustomEvent('modal:opened', { bubbles: true }));
    }

    close() {
      if (!this.modal || !this.isOpen()) return;

      window.cancelAnimationFrame(this.focusFrame);
      this.cartLinkRequest++;
      this.modal.classList.remove('open');
      this.setAttribute('aria-hidden', 'true');
      this.modalTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));

      var opener = this.opener;
      window.requestAnimationFrame(() => {
        if (!this.isOpen() && opener && document.contains(opener)) opener.focus({ preventScroll: true });
      });

      this.dispatchEvent(new CustomEvent('modal:closed', { bubbles: true }));
    }

    isOpen() {
      return this.getAttribute('aria-hidden') === 'false' && this.modal && this.modal.classList.contains('open');
    }

    setFormData(data) {
      Object.keys(data).forEach((key) => {
        var input = this.querySelector('[name="' + key + '"]');
        if (input) input.value = data[key];
      });
    }

    resetForm() {
      if (this.form) this.form.reset();
    }

    async loadCartLink() {
      var request = ++this.cartLinkRequest;
      this.cartLinkReady = false;
      this.cartLinkField.value = '';
      this.cartLinkField.setAttribute('aria-busy', 'true');
      if (this.cartLinkSubmit) this.cartLinkSubmit.disabled = true;
      if (this.cartLinkRetry) this.cartLinkRetry.hidden = true;
      if (this.cartLinkStatus) {
        this.cartLinkStatus.hidden = false;
        this.cartLinkStatus.textContent = 'Preparing your cart link...';
      }
      try {
        var url = await this.generateSharedCartUrl();
        if (request !== this.cartLinkRequest || !this.isConnected || !this.isOpen()) return;
        this.cartLinkField.value = url;
        this.cartLinkReady = true;
        if (this.cartLinkSubmit) this.cartLinkSubmit.disabled = false;
        if (this.cartLinkStatus) this.cartLinkStatus.hidden = true;
      } catch (error) {
        if (request !== this.cartLinkRequest || !this.isConnected || !this.isOpen()) return;
        if (this.cartLinkStatus) {
          this.cartLinkStatus.hidden = false;
          this.cartLinkStatus.textContent = error.name === 'RangeError'
            ? 'This cart has too many options to fit in a shareable link. Please contact us for help.'
            : 'Your cart link could not be prepared. Please try again.';
        }
        if (this.cartLinkRetry) this.cartLinkRetry.hidden = false;
      } finally {
        if (request === this.cartLinkRequest) this.cartLinkField.setAttribute('aria-busy', 'false');
      }
    }

    async generateSharedCartUrl() {
      var cartRoute = ((window.routes && window.routes.cartUrl) || '/cart').replace(/\/$/, '');
      var response = await fetch(cartRoute + '.js');
      if (!response.ok) throw new Error('Cart request failed');
      var cart = await response.json();
      var items = cart.items;
      if (!Array.isArray(items) || !items.length || items.some(function (item) {
        return !Number.isSafeInteger(Number(item.variant_id)) || Number(item.variant_id) <= 0 || !Number.isSafeInteger(item.quantity) || item.quantity <= 0;
      })) throw new Error('Cart items unavailable');
      if (items.length > 50) throw new RangeError('Cart link too large');
      var sharedItems = items.map(function (item) {
        var properties = item.properties || {};
        if (typeof properties !== 'object' || Array.isArray(properties)) throw new Error('Invalid cart properties');
        var keys = Object.keys(properties).sort();
        if (keys.length > 25) throw new RangeError('Cart link too large');
        var shared = { id: Number(item.variant_id), quantity: item.quantity, properties: Object.create(null) };
        keys.forEach(function (key) {
          var value = properties[key];
          if (!key || ['__proto__', 'constructor', 'prototype'].indexOf(key) !== -1) throw new Error('Invalid cart properties');
          if (typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value))) value = String(value);
          if (typeof value !== 'string') throw new Error('Invalid cart properties');
          if (key.length > 255 || value.length > 2048) throw new RangeError('Cart link too large');
          if (value !== '') shared.properties[key] = value;
        });
        if (item.selling_plan_allocation) {
          var plan = item.selling_plan_allocation.selling_plan;
          if (!plan || !Number.isSafeInteger(Number(plan.id)) || Number(plan.id) <= 0) throw new Error('Invalid selling plan');
          shared.selling_plan = Number(plan.id);
        }
        return shared;
      });
      if (sharedItems.some(function (item) { return Object.keys(item.properties).length || item.selling_plan; })) {
        var payload = encodeURIComponent(JSON.stringify({ version: 2, items: sharedItems }));
        if (payload.length > 12000) throw new RangeError('Cart link too large');
        return window.location.origin + cartRoute + '/?shared-cart=true&shared-cart-data=' + payload;
      }
      var itemsParam = items.map(function (item) {
        return item.variant_id + ':' + item.quantity;
      }).join(',');
      return window.location.origin + cartRoute + '/?shared-cart=true&items=' + itemsParam;
    }
  }

  customElements.define('modal-dialog', ModalDialog);
})();
